/**
 * Angular service for User Media API operations
 * 
 * @remarks
 * Provides comprehensive methods for managing user-uploaded media including:
 * - Single and batch media creation
 * - Media retrieval with filtering and pagination
 * - Organization-scoped queries
 * - Search functionality
 * - Statistics and analytics
 * - Soft delete and restore (admin)
 * 
 * All methods return RxJS Observables for reactive programming patterns.
 * Authentication is handled by HTTP interceptors in the consuming application.
 * 
 * @example
 * ```typescript
 * // In your module or standalone component
 * import { provideUserMediaService } from 'cms-post-lib';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideUserMediaService({
 *       apiBaseUrl: environment.apiBaseUrl
 *     })
 *   ]
 * };
 * 
 * // In your component
 * constructor(private mediaService: UserMediaService) {}
 * 
 * ngOnInit() {
 *   this.mediaService.getAllMedias({ orgId: '507f1f77bcf86cd799439011' })
 *     .subscribe(response => {
 *       this.medias = response.data.medias;
 *     });
 * }
 * ```
 * 
 * @see USER_MEDIA_API_REFERENCE.md for complete API documentation
 */

import { Injectable, inject, InjectionToken } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  UserMediaPopulated,
  CreateUserMediaDto,
  BatchCreateUserMediaDto,
  UpdateUserMediaDto,
  GetAllMediasQueryParams,
  GetMediasByOrganizationQueryParams,
  SearchMediasQueryParams,
  CreateMediaResponse,
  BatchCreateMediaResponse,
  MediaListResponse,
  GetMediaResponse,
  UpdateMediaResponse,
  DeleteMediaResponse,
  RestoreMediaResponse,
  MediaStatsResponse
} from '../models/user-media.model';

/**
 * Configuration interface for UserMedia service
 */
export interface UserMediaConfig {
  /**
   * Base URL for the API (without trailing slash)
   * @example 'https://api.example.com'
   */
  apiBaseUrl: string;

  /**
   * Enable debug logging
   * @default false
   */
  debug?: boolean;
}

/**
 * Injection token for UserMedia service configuration
 */
export const USER_MEDIA_API_CONFIG = new InjectionToken<UserMediaConfig>(
  'USER_MEDIA_API_CONFIG',
  {
    providedIn: 'root',
    factory: () => ({
      apiBaseUrl: '',
      debug: false
    })
  }
);

/**
 * User Media API Service
 * 
 * @remarks
 * Manages all user media operations with multi-size URL support.
 * All media is scoped to organizations with automatic access control.
 */
@Injectable({
  providedIn: 'root'
})
export class UserMediaService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(USER_MEDIA_API_CONFIG);
  private readonly baseUrl = `${this.config.apiBaseUrl}/api/user-medias`;

  /**
   * Log debug messages if debug mode is enabled
   */
  private log(message: string, ...args: any[]): void {
    if (this.config.debug) {
      console.log(`[UserMediaService] ${message}`, ...args);
    }
  }

  /**
   * Create a single media item
   * 
   * @remarks
   * Creates a new media with multi-size URLs (large, medium, small, mobile).
   * Current user must belong to the specified organization.
   * User is automatically set as the uploader from auth token.
   * 
   * @param dto - Media creation data
   * @returns Observable of created media with populated references
   * 
   * @example
   * ```typescript
   * const newMedia: CreateUserMediaDto = {
   *   fileName: 'profile-photo.jpg',
   *   orgId: '507f1f77bcf86cd799439011',
   *   urls: {
   *     large: 'https://cdn.example.com/large/photo.jpg',
   *     medium: 'https://cdn.example.com/medium/photo.jpg',
   *     small: 'https://cdn.example.com/small/photo.jpg',
   *     mobile: 'https://cdn.example.com/mobile/photo.jpg'
   *   }
   * };
   * 
   * this.mediaService.createMedia(newMedia).subscribe(
   *   response => console.log('Media created:', response.data.media)
   * );
   * ```
   */
  createMedia(dto: CreateUserMediaDto): Observable<CreateMediaResponse> {
    //this.log('Creating media', dto);
    return this.http.post<CreateMediaResponse>(this.baseUrl, dto).pipe(
      tap(response => this.log('Media created successfully', response.data.media))
    );
  }

  /**
   * Create multiple media items in a single request
   * 
   * @remarks
   * Batch creates media items. User must have access to all specified organizations.
   * Minimum 1 media required in the array.
   * 
   * @param dto - Batch media creation data
   * @returns Observable of created medias with count
   * 
   * @example
   * ```typescript
   * const batchData: BatchCreateUserMediaDto = {
   *   medias: [
   *     {
   *       fileName: 'photo1.jpg',
   *       orgId: '507f1f77bcf86cd799439011',
   *       urls: {
   *         large: 'https://cdn.example.com/large/photo1.jpg',
   *         medium: 'https://cdn.example.com/medium/photo1.jpg',
   *         small: 'https://cdn.example.com/small/photo1.jpg',
   *         mobile: 'https://cdn.example.com/mobile/photo1.jpg'
   *       }
   *     },
   *     {
   *       fileName: 'photo2.jpg',
   *       orgId: '507f1f77bcf86cd799439011',
   *       urls: {
   *         large: 'https://cdn.example.com/large/photo2.jpg',
   *         medium: 'https://cdn.example.com/medium/photo2.jpg',
   *         small: 'https://cdn.example.com/small/photo2.jpg',
   *         mobile: 'https://cdn.example.com/mobile/photo2.jpg'
   *       }
   *     }
   *   ]
   * };
   * 
   * this.mediaService.batchCreateMedias(batchData).subscribe(
   *   response => {
   *     console.log(`${response.data.count} medias created`);
   *     console.log('Medias:', response.data.medias);
   *   }
   * );
   * ```
   */
  batchCreateMedias(dto: BatchCreateUserMediaDto): Observable<BatchCreateMediaResponse> {
    this.log('Batch creating medias', dto);
    return this.http.post<BatchCreateMediaResponse>(`${this.baseUrl}/batch`, dto).pipe(
      tap(response => this.log(`${response.data.count} medias created successfully`))
    );
  }

  /**
   * Get all medias with filtering and pagination
   * 
   * @remarks
   * Returns medias from user's organizations only.
   * Excludes deleted medias by default.
   * Results sorted by creation date (newest first).
   * 
   * @param params - Query parameters for filtering and pagination
   * @returns Observable of media list with pagination
   * 
   * @example
   * ```typescript
   * // Get all medias with pagination
   * this.mediaService.getAllMedias({
   *   page: 1,
   *   limit: 10
   * }).subscribe(response => {
   *   console.log('Medias:', response.data.medias);
   *   console.log('Total pages:', response.data.pagination.pages);
   * });
   * 
   * // Filter by organization and date range
   * this.mediaService.getAllMedias({
   *   orgId: '507f1f77bcf86cd799439011',
   *   startDate: '2024-01-01T00:00:00.000Z',
   *   endDate: '2024-12-31T23:59:59.999Z',
   *   page: 1,
   *   limit: 20
   * }).subscribe(response => {
   *   console.log('Filtered medias:', response.data.medias);
   * });
   * ```
   */
  getAllMedias(params: GetAllMediasQueryParams = {}): Observable<MediaListResponse> {
    this.log('Getting all medias', params);

    let httpParams = new HttpParams();

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    if (params.fileName) {
      httpParams = httpParams.set('fileName', params.fileName);
    }

    if (params.orgId) {
      httpParams = httpParams.set('orgId', params.orgId);
    }

    if (params.startDate) {
      httpParams = httpParams.set('startDate', params.startDate);
    }

    if (params.endDate) {
      httpParams = httpParams.set('endDate', params.endDate);
    }

    return this.http.get<MediaListResponse>(this.baseUrl, { params: httpParams }).pipe(
      tap(response => this.log(`Retrieved ${response.data.medias.length} medias`, response.data.pagination))
    );
  }

  /**
   * Get medias for a specific organization
   * 
   * @remarks
   * User must belong to the organization to access its medias.
   * 
   * @param orgId - MongoDB ObjectId of the organization
   * @param params - Query parameters for pagination
   * @returns Observable of media list with pagination
   * 
   * @example
   * ```typescript
   * this.mediaService.getMediasByOrganization('507f1f77bcf86cd799439011', {
   *   page: 1,
   *   limit: 10
   * }).subscribe(response => {
   *   console.log('Organization medias:', response.data.medias);
   * });
   * ```
   */
  getMediasByOrganization(
    orgId: string,
    params: GetMediasByOrganizationQueryParams = {}
  ): Observable<MediaListResponse> {
    this.log('Getting medias for organization', orgId, params);

    let httpParams = new HttpParams();

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return this.http.get<MediaListResponse>(`${this.baseUrl}/organization/${orgId}`, { params: httpParams }).pipe(
      tap(response => this.log(`Retrieved ${response.data.medias.length} medias for organization`, response.data.pagination))
    );
  }

  /**
   * Search medias by file name
   * 
   * @remarks
   * Searches within a specific organization.
   * User must belong to the organization.
   * 
   * @param params - Search query and filters
   * @returns Observable of matching medias with pagination
   * 
   * @example
   * ```typescript
   * this.mediaService.searchMedias({
   *   q: 'profile',
   *   orgId: '507f1f77bcf86cd799439011',
   *   page: 1,
   *   limit: 10
   * }).subscribe(response => {
   *   console.log('Search results:', response.data.medias);
   * });
   * ```
   */
  searchMedias(params: SearchMediasQueryParams): Observable<MediaListResponse> {
    this.log('Searching medias', params);

    let httpParams = new HttpParams()
      .set('q', params.q)
      .set('orgId', params.orgId);

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return this.http.get<MediaListResponse>(`${this.baseUrl}/search`, { params: httpParams }).pipe(
      tap(response => this.log(`Found ${response.data.medias.length} matching medias`, response.data.pagination))
    );
  }

  /**
   * Get media statistics for an organization
   * 
   * @remarks
   * Returns aggregate statistics including total counts, user breakdown, and recent activity.
   * User must belong to the organization.
   * 
   * @param orgId - MongoDB ObjectId of the organization
   * @returns Observable of media statistics
   * 
   * @example
   * ```typescript
   * this.mediaService.getMediaStats('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     const stats = response.data.stats;
   *     console.log('Total medias:', stats.totalMedias);
   *     console.log('Active medias:', stats.activeMedias);
   *     console.log('Recent uploads:', stats.recentUploads);
   *     console.log('Users:', stats.mediasByUser);
   *   });
   * ```
   */
  getMediaStats(orgId: string): Observable<MediaStatsResponse> {
    this.log('Getting media statistics for organization', orgId);
    return this.http.get<MediaStatsResponse>(`${this.baseUrl}/stats/${orgId}`).pipe(
      tap(response => this.log('Retrieved media statistics', response.data.stats))
    );
  }

  /**
   * Get a specific media by ID
   * 
   * @remarks
   * Media must belong to one of user's organizations.
   * 
   * @param mediaId - MongoDB ObjectId of the media
   * @returns Observable of media with populated references
   * 
   * @example
   * ```typescript
   * this.mediaService.getMediaById('507f191e810c19729de860ea')
   *   .subscribe(response => {
   *     console.log('Media:', response.data.media);
   *     console.log('File name:', response.data.media.fileName);
   *     console.log('URLs:', response.data.media.urls);
   *   });
   * ```
   */
  getMediaById(mediaId: string): Observable<GetMediaResponse> {
    this.log('Getting media by ID', mediaId);
    return this.http.get<GetMediaResponse>(`${this.baseUrl}/${mediaId}`).pipe(
      tap(response => this.log('Retrieved media', response.data.media))
    );
  }

  /**
   * Update a media item
   * 
   * @remarks
   * Can update file name and/or URLs.
   * Media must belong to user's organizations.
   * All fields are optional.
   * 
   * @param mediaId - MongoDB ObjectId of the media
   * @param dto - Updated media data
   * @returns Observable of updated media
   * 
   * @example
   * ```typescript
   * const updates: UpdateUserMediaDto = {
   *   fileName: 'updated-photo.jpg',
   *   urls: {
   *     large: 'https://cdn.example.com/large/updated.jpg',
   *     medium: 'https://cdn.example.com/medium/updated.jpg',
   *     small: 'https://cdn.example.com/small/updated.jpg',
   *     mobile: 'https://cdn.example.com/mobile/updated.jpg'
   *   }
   * };
   * 
   * this.mediaService.updateMedia('507f191e810c19729de860ea', updates)
   *   .subscribe(response => {
   *     console.log('Media updated:', response.data.media);
   *   });
   * ```
   */
  updateMedia(mediaId: string, dto: UpdateUserMediaDto): Observable<UpdateMediaResponse> {
    this.log('Updating media', mediaId, dto);
    return this.http.put<UpdateMediaResponse>(`${this.baseUrl}/${mediaId}`, dto).pipe(
      tap(response => this.log('Media updated successfully', response.data.media))
    );
  }

  /**
   * Delete a media item (soft delete)
   * 
   * @remarks
   * Performs soft delete: sets isDeleted: true and deletedAt: Date.
   * Media is not physically removed and can be restored by admin.
   * Media must belong to user's organizations.
   * 
   * @param mediaId - MongoDB ObjectId of the media
   * @returns Observable of deletion confirmation
   * 
   * @example
   * ```typescript
   * this.mediaService.deleteMedia('507f191e810c19729de860ea')
   *   .subscribe(response => {
   *     console.log(response.message); // "Media deleted successfully"
   *   });
   * ```
   */
  deleteMedia(mediaId: string): Observable<DeleteMediaResponse> {
    this.log('Deleting media', mediaId);
    return this.http.delete<DeleteMediaResponse>(`${this.baseUrl}/${mediaId}`).pipe(
      tap(response => this.log('Media deleted successfully', response.message))
    );
  }

  /**
   * Restore a deleted media (admin only)
   * 
   * @remarks
   * Undeletes media by setting isDeleted: false and clearing deletedAt.
   * Requires admin role.
   * 
   * @param mediaId - MongoDB ObjectId of the media
   * @returns Observable of restored media
   * 
   * @example
   * ```typescript
   * this.mediaService.restoreMedia('507f191e810c19729de860ea')
   *   .subscribe(response => {
   *     console.log('Media restored:', response.data.media);
   *   });
   * ```
   */
  restoreMedia(mediaId: string): Observable<RestoreMediaResponse> {
    this.log('Restoring media', mediaId);
    return this.http.post<RestoreMediaResponse>(`${this.baseUrl}/${mediaId}/restore`, {}).pipe(
      tap(response => this.log('Media restored successfully', response.data.media))
    );
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get medias for the current page with default pagination
   * 
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of paginated medias
   * 
   * @example
   * ```typescript
   * this.mediaService.getMediasPage(2, 20).subscribe(response => {
   *   console.log('Page 2 medias:', response.data.medias);
   * });
   * ```
   */
  getMediasPage(page = 1, limit = 10): Observable<MediaListResponse> {
    this.log('Getting medias page', page, 'with limit', limit);
    return this.getAllMedias({ page, limit });
  }

  /**
   * Get medias by file name filter
   * 
   * @param fileName - File name to filter by
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of filtered medias
   * 
   * @example
   * ```typescript
   * this.mediaService.getMediasByFileName('profile').subscribe(response => {
   *   console.log('Filtered medias:', response.data.medias);
   * });
   * ```
   */
  getMediasByFileName(fileName: string, page = 1, limit = 10): Observable<MediaListResponse> {
    this.log('Getting medias by file name', fileName);
    return this.getAllMedias({ fileName, page, limit });
  }

  /**
   * Get medias within a date range
   * 
   * @param startDate - Start date (ISO string)
   * @param endDate - End date (ISO string)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of date-filtered medias
   * 
   * @example
   * ```typescript
   * this.mediaService.getMediasByDateRange(
   *   '2024-01-01T00:00:00.000Z',
   *   '2024-01-31T23:59:59.999Z'
   * ).subscribe(response => {
   *   console.log('January medias:', response.data.medias);
   * });
   * ```
   */
  getMediasByDateRange(startDate: string, endDate: string, page = 1, limit = 10): Observable<MediaListResponse> {
    this.log('Getting medias by date range', { startDate, endDate });
    return this.getAllMedias({ startDate, endDate, page, limit });
  }

  /**
   * Get organization medias with default pagination
   * 
   * @param orgId - MongoDB ObjectId of the organization
   * @returns Observable of organization medias (first page, 10 items)
   * 
   * @example
   * ```typescript
   * this.mediaService.getOrgMedias('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     console.log('Organization medias:', response.data.medias);
   *   });
   * ```
   */
  getOrgMedias(orgId: string): Observable<MediaListResponse> {
    this.log('Getting organization medias with defaults', orgId);
    return this.getMediasByOrganization(orgId);
  }

  /**
   * Update media file name only
   * 
   * @param mediaId - MongoDB ObjectId of the media
   * @param fileName - New file name
   * @returns Observable of updated media
   * 
   * @example
   * ```typescript
   * this.mediaService.updateFileName('507f191e810c19729de860ea', 'new-name.jpg')
   *   .subscribe(response => {
   *     console.log('File name updated:', response.data.media.fileName);
   *   });
   * ```
   */
  updateFileName(mediaId: string, fileName: string): Observable<UpdateMediaResponse> {
    this.log('Updating media file name', mediaId, fileName);
    return this.updateMedia(mediaId, { fileName });
  }

  /**
   * Update media URLs only
   * 
   * @param mediaId - MongoDB ObjectId of the media
   * @param urls - New multi-size URLs
   * @returns Observable of updated media
   * 
   * @example
   * ```typescript
   * const newUrls = {
   *   large: 'https://cdn.example.com/large/new.jpg',
   *   medium: 'https://cdn.example.com/medium/new.jpg',
   *   small: 'https://cdn.example.com/small/new.jpg',
   *   mobile: 'https://cdn.example.com/mobile/new.jpg'
   * };
   * 
   * this.mediaService.updateUrls('507f191e810c19729de860ea', newUrls)
   *   .subscribe(response => {
   *     console.log('URLs updated:', response.data.media.urls);
   *   });
   * ```
   */
  updateUrls(mediaId: string, urls: UpdateUserMediaDto['urls']): Observable<UpdateMediaResponse> {
    this.log('Updating media URLs', mediaId);
    return this.updateMedia(mediaId, { urls });
  }
}

/**
 * Provider function for UserMedia service
 * 
 * @remarks
 * Use this function to configure the UserMediaService in your application.
 * 
 * @param config - UserMedia service configuration
 * @returns Provider configuration
 * 
 * @example
 * ```typescript
 * import { provideUserMediaService } from 'cms-post-lib';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideUserMediaService({
 *       apiBaseUrl: environment.apiBaseUrl,
 *       debug: !environment.production
 *     })
 *   ]
 * };
 * ```
 */
export function provideUserMediaService(config: UserMediaConfig) {
  return {
    provide: USER_MEDIA_API_CONFIG,
    useValue: config
  };
}
